import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Admin {
    id: number;
    name: string;
    email: string;
}

interface Role {
    id: number;
    role: string;
    description: string;
}

const ManageStaff: React.FC = () => {
    const { id: leagueId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [admins, setAdmins] = useState<Admin[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeAdmin, setActiveAdmin] = useState<Admin | null>(null);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const [assignedRoles, setAssignedRoles] = useState<number[]>([]);

    // Fetch all admins
    useEffect(() => {
        const fetchAdmins = async () => {
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const res = await fetch(`${apiUrl}/manage-staff/admins`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setAdmins(await res.json());
            }
        };
        fetchAdmins();
    }, []);

    // Fetch all roles
    useEffect(() => {
        const fetchRoles = async () => {
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const res = await fetch(`${apiUrl}/manage-staff/roles`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setRoles(await res.json());
            }
        };
        fetchRoles();
    }, []);

   

    // Fetch assigned roles for selected admin
    useEffect(() => {
        if (!activeAdmin) {
            setAssignedRoles([]);
            setSelectedRoles([]);
            return;
        }
        const fetchAssignedRoles = async () => {
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const res = await fetch(`${apiUrl}/manage-staff/${leagueId}/${activeAdmin.id}/roles`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                let data = await res.json();
                // Ensure it's always an array of numbers
                if (Array.isArray(data)) {
                    setAssignedRoles(data.map(Number));
                } else if (typeof data === 'string') {
                    try {
                        const parsed = JSON.parse(data);
                        setAssignedRoles(Array.isArray(parsed) ? parsed.map(Number) : []);
                    } catch {
                        setAssignedRoles([]);
                    }
                } else {
                    setAssignedRoles([]);
                }
                setSelectedRoles([]); // Reset selection when admin changes
            } else {
                setAssignedRoles([]);
                setSelectedRoles([]);
            }
        };
        fetchAssignedRoles();
    }, [activeAdmin, leagueId]);

    // Filter admins by search
    const filteredAdmins = admins.filter(
        admin =>
            admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            admin.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Move selected roles to assigned
    const handleAssignRoles = () => {
        setAssignedRoles([...assignedRoles, ...selectedRoles]);
        setSelectedRoles([]);
    };

    // Remove roles from assigned
    const handleRemoveRoles = (rolesToRemove: number[]) => {
        setAssignedRoles(assignedRoles.filter(id => !rolesToRemove.includes(id)));
    };

    // Save assigned roles to backend
    const handleSave = async () => {
        if (!activeAdmin) return;
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        await fetch(`${apiUrl}/manage-staff/${leagueId}/${activeAdmin.id}/roles`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ roleIds: assignedRoles })
        });
        // Optionally show a success message or reload
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
            {/* Header */}
            <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center py-6">
                    <button
                        onClick={() => navigate(`/admin/leagues/${leagueId}/manage`)}
                        className="text-white hover:text-blue-300 transition duration-200 mr-4"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-3xl font-bold text-white">{t('admin.manageStaff.title', 'Manage Staff')}</h1>
                </div>
            </div>

              {/* Help Card */}

      <div
        className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
        onClick={() => setShowHelpModal(true)}
      >
        <span className="text-5xl text-yellow-300 font-bold">?</span>
      </div>
      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('admin.managingLeagueStaffHelp')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.managingLeagueStaffHelpContent')}
            </div>
          
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Admins Card */}
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 flex flex-col">
                    <h2 className="text-xl font-bold text-white mb-4">{t('admin.manageStaff.admins', 'Admins')}</h2>
                    <input
                        type="text"
                        placeholder={t('admin.manageStaff.searchPlaceholder', 'Search by name or email')}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300 mb-4"
                    />
                    <div className="flex-1 overflow-y-auto">
                        {filteredAdmins.map(admin => (
                            <div
                                key={admin.id}
                                className={`p-3 rounded-lg mb-2 cursor-pointer transition ${activeAdmin?.id === admin.id
                                    ? 'bg-blue-600/30 text-white border border-blue-400'
                                    : 'bg-white/10 text-blue-200 hover:bg-blue-600/10'
                                    }`}
                                onClick={() => setActiveAdmin(admin)}
                            >
                                <div className="font-semibold">{admin.name}</div>
                                <div className="text-xs">{admin.email}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Only show roles UI if an admin is selected */}
                {activeAdmin && (
                    <>
                        {/* Roles Card */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 flex flex-col">
                            <h2 className="text-xl font-bold text-white mb-4">{t('admin.manageStaff.roles', 'Available Roles')}</h2>
                            <div className="flex-1 overflow-y-auto">
                                {roles
                                    .filter(role => !assignedRoles.includes(role.id))
                                    .map(role => (
                                        <div
                                            key={role.id}
                                            className={`p-3 rounded-lg mb-2 cursor-pointer transition ${selectedRoles.includes(role.id)
                                                ? 'bg-purple-600/30 text-white border border-purple-400'
                                                : 'bg-white/10 text-blue-200 hover:bg-purple-600/10'
                                                }`}
                                            onClick={() =>
                                                setSelectedRoles(selectedRoles.includes(role.id)
                                                    ? selectedRoles.filter(id => id !== role.id)
                                                    : [...selectedRoles, role.id])
                                            }
                                        >
                                            <div className="font-semibold">{role.role}</div>
                                            <div className="text-xs">{role.description}</div>
                                        </div>
                                    ))}
                            </div>
                            <button
                                disabled={selectedRoles.length === 0}
                                onClick={handleAssignRoles}
                                className={`mt-4 flex items-center justify-center px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium border border-green-400/60 transition duration-200 ${selectedRoles.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                <span>{t('admin.manageStaff.assign', 'Assign')}</span>
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>

                        {/* Assigned Roles Card */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 flex flex-col">
                            <h2 className="text-xl font-bold text-white mb-4">{t('admin.manageStaff.assignedRoles', 'Assigned Roles')}</h2>
                            <div className="flex-1 overflow-y-auto">
                                {roles
                                    .filter(role => assignedRoles.includes(role.id))
                                    .map(role => (
                                        <div
                                            key={role.id}
                                            className="p-3 rounded-lg mb-2 cursor-pointer bg-green-600/20 text-white border border-green-400"
                                            onClick={() => handleRemoveRoles([role.id])}
                                        >
                                            <div className="font-semibold">{role.role}</div>
                                            <div className="text-xs">{role.description}</div>
                                        </div>
                                    ))}
                            </div>
                            <button
                                disabled={assignedRoles.length === 0}
                                onClick={() => handleRemoveRoles(assignedRoles)}
                                className={`mt-4 flex items-center justify-center px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium border border-purple-400/60 transition duration-200 ${assignedRoles.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span>{t('admin.manageStaff.remove', 'Remove')}</span>
                            </button>
                            <button
                                disabled={!activeAdmin}
                                onClick={handleSave}
                                className={`mt-6 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold border border-blue-400/60 transition duration-200 ${!activeAdmin ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                {t('admin.manageStaff.save', 'Save')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ManageStaff;